/**************************************************************************************************/
/*   File: /src/app/stores/selectors/editing/selectors.ts                                         */
/*   Project: newplan-frontend                                                                    */
/*   Created Date: Monday April 29th 2019                                                         */
/*   Author: Andrea Zamboni andrea.zamboni@trilogis.it                                            */
/*   -----                                                                                        */
/*   Last Modified: Fri May 24 2019                                                               */
/*   Modified By: Andrea Zamboni                                                                  */
/*   -----                                                                                        */
/*   Copyright (c) 2019 Trilogis s.r.l. for SIAG                                                  */
/**************************************************************************************************/
import { createFeatureSelector, createSelector } from '@ngrx/store';

import * as fromReducers from '../../reducers/editing';
import * as fromStates from '../../states/editing';
import { DossierContent } from '../../../shared/models';

/**
 * Get the dossier editing State
 */
export const getDossierEditingState = createFeatureSelector<fromStates.EditingState>('editing');

/**
 * Get is loading
 */
export const getIsLoading = createSelector(
    getDossierEditingState,
    fromReducers.helpers.getIsLoading
);
/**
 * Get messages
 */
export const getMessages = createSelector(
    getDossierEditingState,
    fromReducers.helpers.getMessages
);
/**
 * Get is validating
 */
export const getIsValidating = createSelector(
    getDossierEditingState,
    fromReducers.helpers.getIsValidating
);
/**
 * Get is validated
 */
export const getIsValidated = createSelector(
    getDossierEditingState,
    fromReducers.helpers.getIsValidated
);
/**
 * Get is loaded
 */
export const getIsLoaded = createSelector(
    getDossierEditingState,
    fromReducers.helpers.getIsLoaded
);
/**
 * Get dossier
 */
export const getDossier = createSelector(
    getDossierEditingState,
    fromReducers.helpers.getDossier
);
/**
 * Get the current dossier id
 */
export const getDossierId = createSelector(
    getDossierEditingState,
    fromReducers.helpers.getDossierId
);
/**
 * Get dossier status
 */
export const getDossierStatus = createSelector(
    getDossierEditingState,
    fromReducers.helpers.getDossierStatus
);
/**
 * Get spatial files
 */
export const getSpatialFiles = createSelector(
    getDossierEditingState,
    fromReducers.helpers.getSpatialFiles
);
/**
 * Get spatial upload finished
 */
export const getSpatialUploadFinished = createSelector(
    getDossierEditingState,
    fromReducers.helpers.getSpatialUploadFinished
);
/**
 * Get other files
 */
export const getOtherFiles = createSelector(
    getDossierEditingState,
    fromReducers.helpers.getOtherFiles
);
/**
 * Get other upload finished
 */
export const getOtherUploadFinished = createSelector(
    getDossierEditingState,
    fromReducers.helpers.getOtherUploadFinished
);

/**
 * Is set plan code
 */
export const isSetPlanCode = createSelector(
    getDossier,
    (dossier) => {
        return dossier !== undefined &&
            dossier.summary !== undefined &&
            dossier.summary.plan !== undefined &&
            dossier.summary.plan.code !== undefined;
    }
);
/**
 * Is get Selected plan 
 */
export const getSelectedPlan = createSelector(
    getDossier,
    (dossier) => {
        return dossier !== undefined &&
            dossier.summary !== undefined &&
            dossier.summary.plan !== undefined ? dossier.summary.plan : undefined;
    }
);
/**
 * Is set ambito code
 */
export const isSetAmbitoCode = createSelector(
    getDossier,
    (dossier) => {
        return dossier !== undefined &&
            dossier.summary !== undefined &&
            dossier.summary.territorialAreas !== undefined &&
            dossier.summary.territorialAreas.length > 0;
    }
);
/**
 * Is getAmbitiCodes 
 */
export const getAmbitiCodes = createSelector(
    getDossier,
    (dossier) => {
        return dossier !== undefined &&
            dossier.summary !== undefined &&
            dossier.summary.territorialAreas !== undefined &&
            dossier.summary.territorialAreas.length > 0 ? dossier.summary.territorialAreas : [];
    }
);
/**
 * Is set spatial data type
 */
export const isSetSpatialDataType = createSelector(
    getDossier,
    (dossier) => {
        return dossier !== undefined &&
            dossier.spatialData !== undefined &&
            dossier.spatialData.spatialDataType !== undefined;
    }
);
/**
 * Get the spatial data
 */
export const getSpatialData = createSelector(
    getDossier,
    (dossier) => {
        return dossier !== undefined &&
            dossier.spatialData !== undefined ? dossier.spatialData : undefined;
    }
);
/**
 * Get the spatial data
 */
export const getSpatialDataReport = createSelector(
    getDossier,
    (dossier) => {
        return dossier !== undefined &&
            dossier.spatialDataReport !== undefined ? dossier.spatialDataReport : undefined;
    }
);
/**
 * Get the spatial informative report
 */
export const getSpatialInformativeReport = createSelector(
    getDossier,
    (dossier) => {
        return dossier !== undefined &&
            dossier.spatialInformativeReport !== undefined ? dossier.spatialInformativeReport : undefined;
    }
);
/**
 * Get the other data
 */
export const getOtherDataFile = createSelector(
    getDossier,
    (dossier) => {
        return dossier !== undefined &&
            dossier.other !== undefined ? dossier.other : undefined;
    }
);
/**
 * Get the other data
 */
export const getTerritorialAreasRegulations = createSelector(
    getDossier,
    (dossier) => {
        return dossier !== undefined &&
            dossier.territorialAreasRegulations !== undefined &&
            Object.keys(dossier.territorialAreasRegulations).length > 0 ?
            dossier.territorialAreasRegulations :
            undefined;
    }
);
/**
 * Is spatial dossier
 */
export const isSpatialDossier = createSelector(
    getDossier,
    (dossier) => {
        return dossier !== undefined &&
            dossier.summary !== undefined &&
            dossier.summary.spatialDossier;
    }
);
/**
 * Is regulations dossier
 */
export const isRegulationsDossier = createSelector(
    getDossier,
    (dossier) => {
        return dossier !== undefined &&
            dossier.summary !== undefined &&
            dossier.summary.regulationsDossier;
    }
);
/**
 * Get summary
 */
export const getSummary = createSelector(
    getDossier,
    (dossier) => {
        return dossier !== undefined &&
            dossier.summary !== undefined ?
            dossier.summary : undefined;
    }
);
/**
 * Spatial data is changed
 */
export const getDossierHasSpatialChange = createSelector(
    getDossierEditingState,
    fromReducers.helpers.getDossierHasSpatialChange
);
/**
 * Other data is changed 
 */
export const getDossierHasOtherChange = createSelector(
    getDossierEditingState,
    fromReducers.helpers.getDossierHasOtherChange
);
/**
 * Get the regulations file
 */
export const getRegulationsFiles = createSelector(
    getDossierEditingState,
    fromReducers.helpers.getRegulationsFiles
);
/**
 * Get the dossier has regulations change
 */
export const getDossierHasRegulationChange = createSelector(
    getDossierEditingState,
    fromReducers.helpers.getDossierHasRegulationChange
);
/**
 * Get the regulation area change
 */
export const getRegulationAreaChanged = createSelector(
    getDossierEditingState,
    fromReducers.helpers.getRegulationAreaChanged
);
/**
 * Get true if is set spatial files and is uploaded
 */
export const isSetSpatialFilesAndUploaded = createSelector(
    getSpatialFiles,
    (files) => {
        if (files && files.length > 0) {
            return files.map(
                file => file.isUploaded
            ).reduce(
                (accumulator, currentValue) => accumulator && currentValue
            );
        }

        return false;
    }
);
/**
 * Get true if is set other files and is uploaded
 */
export const isSetOtherFilesAndUploaded = createSelector(
    getOtherFiles,
    (files) => {
        if (files && files.length > 0) {
            return files.map(
                file => file.isUploaded
            ).reduce(
                (accumulator, currentValue) => accumulator && currentValue
            );
        }

        return false;
    }
);